import z from "zod";

import { OpeningHours, Supplier, Weekday } from "@/types/supplier";

export const WEEKDAYS: Array<{ day: Weekday; label: string }> = [
  { day: 0, label: "Sunday" },
  { day: 1, label: "Monday" },
  { day: 2, label: "Tuesday" },
  { day: 3, label: "Wednesday" },
  { day: 4, label: "Thursday" },
  { day: 5, label: "Friday" },
  { day: 6, label: "Saturday" },
];

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const MIDNIGHT_INPUT = "00:00";
const MIDNIGHT_CLOSE = "24:00";
const MINUTES_PER_DAY = 24 * 60;

const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);

  return hours * 60 + minutes;
};

/** A time input cannot hold 24:00, so a 00:00 close means midnight at the end of the day. */
const closeMinutes = (time: string): number =>
  time === MIDNIGHT_INPUT ? MINUTES_PER_DAY : toMinutes(time);

export const SUPPLIER_FORM_SCHEMA = z
  .object({
    name: z.string().trim().min(1, "Name is required"),
    isActive: z.boolean(),
    margin: z
      .number({ invalid_type_error: "Margin is required" })
      .min(0, "Margin can't be negative"),
    cap: z
      .number({ invalid_type_error: "Cap is required" })
      .min(0, "Cap can't be negative"),
    alwaysOpen: z.boolean(),
    days: z.array(
      z.object({
        windows: z.array(z.object({ open: z.string(), close: z.string() })),
      }),
    ),
  })
  .superRefine((form, ctx) => {
    if (form.alwaysOpen) return;

    const issue = (path: Array<string | number>, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

    form.days.forEach((day, dayIndex) => {
      const path = (index: number, field: "open" | "close") => [
        "days",
        dayIndex,
        "windows",
        index,
        field,
      ];
      const invalid = day.windows.flatMap((window, index) =>
        (["open", "close"] as const)
          .filter((field) => !TIME_PATTERN.test(window[field]))
          .map((field) => ({ index, field })),
      );

      invalid.forEach(({ index, field }) =>
        issue(path(index, field), "Pick a time"),
      );

      if (invalid.length > 0) return;

      const sorted = day.windows
        .map((window, index) => ({
          index,
          open: toMinutes(window.open),
          close: closeMinutes(window.close),
        }))
        .sort((a, b) => a.open - b.open);

      sorted.forEach((window, position) => {
        if (window.close <= window.open) {
          issue(path(window.index, "close"), "Close after it opens");
        } else if (position > 0 && window.open < sorted[position - 1].close) {
          issue(path(window.index, "open"), "Overlaps other hours");
        }
      });
    });
  });

export type SupplierFormData = z.infer<typeof SUPPLIER_FORM_SCHEMA>;

export const toSupplierFormValues = (
  supplier: Supplier | null,
): SupplierFormData => ({
  name: supplier?.name ?? "",
  isActive: supplier?.isActive ?? true,
  // numeric columns come back as strings; new suppliers get the column defaults
  margin: supplier ? Number(supplier.margin) : 35,
  cap: supplier ? Number(supplier.cap) : 10,
  alwaysOpen: !supplier || supplier.openingHours === null,
  days: WEEKDAYS.map(({ day }) => ({
    windows: (supplier?.openingHours?.[day] ?? []).map(({ open, close }) => ({
      open,
      close: close === MIDNIGHT_CLOSE ? MIDNIGHT_INPUT : close,
    })),
  })),
});

export const toOpeningHours = (form: SupplierFormData): OpeningHours | null =>
  form.alwaysOpen
    ? null
    : Object.fromEntries(
        WEEKDAYS.map(({ day }) => [
          day,
          form.days[day].windows.map(({ open, close }) => ({
            open,
            close: close === MIDNIGHT_INPUT ? MIDNIGHT_CLOSE : close,
          })),
        ]),
      );
